import React from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import memoize from 'memoize-one';
import { MDText } from 'i18n-react';
import noop from 'lodash/noop';
import debounce from 'lodash/debounce';
import get from 'lodash/get';
import BaseTable from 'react-base-table/lib/BaseTable';
import SortOrder from 'react-base-table/lib/SortOrder';
import AutoResizer from 'react-base-table/lib/AutoResizer';

import { ThemeConsumer, Themes } from '@adsk/bim360-matrix-react-helpers/es/theme';
import ActionButton from '@adsk/bim360-matrix-react-action-button';
import EmptyState from '@adsk/bim360-matrix-react-empty-state';
import Loader from '@adsk/bim360-matrix-react-loader';

import Column, { Alignment, FrozenDirection } from './Column';
import TableCell from './TableCell';
import TableHeaderCell from './TableHeaderCell';
import ExpandIcon from './ExpandIcon';
import SortIndicator from './SortIndicator';
import SelectionHeaderCell from './SelectionHeaderCell';
import SelectionCell from './SelectionCell';
import ActionHeaderCell from './ActionHeaderCell';
import ActionCell from './ActionCell';
import ContextMenu from './ContextMenu';

import {
  normalizeColumns,
  renderElement,
  isObjectEqual,
  callOrReturn,
  unflatten,
  groupByColumns,
  isGroupKey,
  cloneArray,
  mapColumnsGroupPath
} from './utils';

import Storage from '@adsk/bim360-matrix-react-helpers/es/Storage';
import { withLocale, TSymbolProp } from '@adsk/bim360-matrix-react-helpers/es/locale';

import './_MatrixTable.scss';

export const ActionMode = {
  /**
   * Show action options in the action column
   */
  COLUMN: 'column',
  /**
   * Show action options in the context menu
   */
  CONTEXT: 'context',
  /**
   * Show action options in both action column and context menu
   */
  BOTH: 'both'
};

/**
 * Different values for the `align` property of the `scrollToRow` method (default is `auto`)
 */
export const ScrollToRowAlignment = {
  /**
   * Scroll as little as possible to ensure the row is visible.
   *  (If the row is already visible, it won't scroll at all.)
   */
  AUTO: 'auto',
  /**
   * If the row is already visible, don't scroll at all. If it is less than one viewport away,
   *  scroll as little as possible so that it becomes visible.
   *  If it is more than one viewport away, scroll so that it is centered within the grid.
   */
  SMART: 'smart',
  /**
   * Center align the row within the table.
   */
  CENTER: 'center',
  /**
   * Align the row to the bottom, right hand side of the table.
   */
  END: 'end',
  /**
   * Align the row to the top, left hand of the table.
   */
  START: 'start'
};

// width of selectionColumn and actionColumn,
// 16 - icon width, 15 - one side padding, 7.5 - another side padding, to make the gutter width be 15
// should override it via custom props if you want to change the gutter width
const DEFAULT_COLUMN_WIDTH = 16 + 15 + 7.5;
const SELECTION_COLUMN_KEY = '__matrixtable_selection__';
const ACTION_COLUMN_KEY = '__matrixtable_action__';
// the shadow color is $bim-gray-lighter
const GROUP_BOX_SHADOW = '2px 0 0 #EDF0F2';

const addGroupStyle = columns => {
  if (columns.length < 2) return;
  for (let columnIndex = 0; columnIndex < columns.length - 1; columnIndex++) {
    const column = columns[columnIndex];
    const nextColumn = columns[columnIndex + 1];
    if (
      column.key !== SELECTION_COLUMN_KEY &&
      nextColumn.key !== ACTION_COLUMN_KEY &&
      column.groupKey !== nextColumn.groupKey
    ) {
      column.style = {
        ...column.style,
        boxShadow: GROUP_BOX_SHADOW,
        WebkitBoxShadow: GROUP_BOX_SHADOW
      };
    }
  }
};

/**
 * Table component for Matrix Design, extends [BaseTable](https://autodesk.github.io/react-base-table/)
 */
export class MatrixTable extends React.PureComponent {
  constructor(props) {
    super(props);

    const columns = this._normalizeColumns(props);
    const {
      selectedRowKeys,
      defaultSelectedRowKeys,
      expandedRowKeys,
      defaultExpandedRowKeys
    } = props;
    const state = {
      selectedRowKeys: cloneArray(
        props.selectedRowKeys !== undefined ? selectedRowKeys : defaultSelectedRowKeys
      ),
      expandedRowKeys: cloneArray(
        props.expandedRowKeys !== undefined ? expandedRowKeys : defaultExpandedRowKeys
      ),
      expandedGroupKeys: []
    };

    this.renderEmpty = this.renderEmpty.bind(this);
    this.renderOverlay = this.renderOverlay.bind(this);
    this.renderHeader = this.renderHeader.bind(this);

    this._setTableRef = this._setTableRef.bind(this);
    this._setMenuRef = this._setMenuRef.bind(this);
    this._rowClassName = this._rowClassName.bind(this);
    this._headerClassName = this._headerClassName.bind(this);
    this._handleSelectAllChange = this._handleSelectAllChange.bind(this);
    this._handleSelectChange = this._handleSelectChange.bind(this);
    this._handleColumnResize = this._handleColumnResize.bind(this);
    this._handleColumnToggle = this._handleColumnToggle.bind(this);
    this._handleColumnFrozen = this._handleColumnFrozen.bind(this);
    this._handleColumnReorder = this._handleColumnReorder.bind(this);
    this._storeColumnsState = debounce(this._storeColumnsState, 250);
    // if update data and loading are in two different actions, then there is gap between them,
    // so we have to debounce for relative time to wait for loading status being updated.
    this._handleEndReached = debounce(this._handleEndReached.bind(this), 10);
    this._handleRowContextMenu = this._handleRowContextMenu.bind(this);
    this._handleRowExpand = this._handleRowExpand.bind(this);

    this._headerCellProps = this._headerCellProps.bind(this);
    this._rowProps = this._rowProps.bind(this);
    this._cellProps = this._cellProps.bind(this);

    this._getColumnsWithState = memoize((columns, columnsState, fixed) => {
      const cols = columns.map(column => ({ ...column, ...columnsState[column.key] }));

      if (cols.some(col => !!col.groupKey)) {
        const visiableCols = cols.filter(col => !col.hidden);
        if (fixed) {
          const mainCols = visiableCols.filter(col => !col.frozen);
          const leftFrozenCols = visiableCols.filter(
            col => col.frozen === FrozenDirection.LEFT || col.frozen === true
          );
          const rightFrozenCols = visiableCols.filter(col => col.frozen === FrozenDirection.RIGHT);

          addGroupStyle(mainCols);
          addGroupStyle(leftFrozenCols);
          addGroupStyle(rightFrozenCols);
        } else {
          addGroupStyle(visiableCols);
        }
      }

      return cols;
    });
    this._concatExpandedKeys = memoize((keysA, keysB) => [].concat(keysA, keysB));
    // get headerHeight array to show column group header
    this._getHeaderHeight = memoize((headerHeight, headerGroupHeight, level) => {
      const heights = [];
      for (let i = 0; i < level; i++) heights.push(headerGroupHeight);
      heights.push(headerHeight);
      return heights;
    });

    this._rowEventHandlers = {
      onContextMenu: props.actionOptions ? this._handleRowContextMenu : undefined,
      onClick: props.onRowClick,
      ...props.rowEventHandlers
    };

    this._components = {
      TableCell,
      TableHeaderCell,
      ExpandIcon,
      SortIndicator,
      ...props.components
    };

    this._cachedColumnsState = {};
    const columnsState = {};
    let storedColumnsState;
    if (props.storageKey && typeof localStorage !== 'undefined') {
      this.storage = new Storage(props.storageKey);
      storedColumnsState = this.storage.get('columnsState', {});
    }
    columns.forEach((column, index) => {
      const { key, width, hidden, frozen, resizable, locked, toggleable } = column;
      this._cachedColumnsState[key] = { width, hidden, frozen, resizable, locked, toggleable };

      if (key === props.expandColumnKey) {
        column.toggleable = false;
        column.hidden = false;
      }
      if (this.storage) {
        const columnState = storedColumnsState[key];
        if (columnState) {
          if (column.resizable) {
            column.width = columnState.width;
          }
          if (!column.locked && column.toggleable !== false) {
            column.hidden = columnState.hidden;
          }
          if (!column.locked) {
            column.frozen = columnState.frozen;
          }

          if (this.props.columnsReorderable) {
            column.position = columnState.position || index;
          }
        }
      }
      {
        const { width, hidden, frozen, position } = column;
        columnsState[key] = { width, hidden, frozen, position };
      }
    });

    if (this.props.columnsReorderable) {
      columns.sort((colA, colB) => {
        if (colB.position < colA.position) {
          return 1;
        } else if (colB.position > colA.position) {
          return -1;
        } else {
          return 0;
        }
      });
    }

    this._data = props.data;
    this._groups = [];
    if (this._isGrouping(props)) {
      const { data, groupBy, expandColumnKey, emptyGroupText, rowKey } = props;
      const [grouped, groups] = groupByColumns(
        data,
        groupBy,
        columns,
        expandColumnKey,
        emptyGroupText || props[TSymbolProp].translate('EMPTY_GROUP'),
        rowKey
      );
      this._proxyColumns(columns, props);
      this._data = unflatten(grouped.concat(groups), null, rowKey);
      this._groups = groups;
      state.expandedGroupKeys = groups.map(group => group[rowKey]);
    }
    this._columnsGroupPath = {};
    this._columnGroupsLevel = 0;

    state.columns = columns;
    state.columnsState = columnsState;
    if (this.storage) {
      this.storage.set('columnsState', columnsState);
    }

    this.state = state;
  }

  _normalizeColumns(props) {
    const columns = props.columns
      ? props.columns.map(column => ({ ...column }))
      : normalizeColumns(props.children);

    this._insertSelectionColumn(columns, props);
    this._insertActionColumn(columns, props);
    return columns;
  }

  _proxyColumns(columns, props) {
    columns.forEach(column => {
      const { cellRenderer, dataGetter } = column;

      if (!cellRenderer && !dataGetter) {
        if (column.dataKey === props.rowKey) {
          // to make the rowKey not be displayed in group rows
          column.dataGetter = args => {
            if (isGroupKey(args.rowData[props.rowKey])) return null;
            return args.rowData[props.rowKey];
          };
        }
      }
      if (cellRenderer && !column.__proxy_cellRenderer__) {
        column.cellRenderer = args => {
          if (isGroupKey(args.rowData[props.rowKey])) {
            if (column.key === props.expandColumnKey || column.groupAggregator) {
              return get(args.rowData, column.dataKey);
            }
            return null;
          }
          return renderElement(cellRenderer, args);
        };
        column.__proxy_cellRenderer__ = cellRenderer;
      }
      if (dataGetter && !column.__proxy_dataGetter__) {
        column.dataGetter = args => {
          if (isGroupKey(args.rowData[props.rowKey])) {
            if (column.key === props.expandColumnKey || column.groupAggregator) {
              return get(args.rowData, column.dataKey);
            }
            return null;
          }
          return dataGetter(args);
        };
        column.__proxy_dataGetter__ = dataGetter;
      }
    });
  }

  _insertSelectionColumn(columns, props) {
    if (!props.selectable) return;

    columns.unshift({
      width: DEFAULT_COLUMN_WIDTH,
      flexShrink: 0,
      locked: true,
      resizable: false,
      frozen: FrozenDirection.LEFT,
      headerRenderer: () => (
        <SelectionHeaderCell
          data={this.getExpandedState().expandedData}
          rowKey={props.rowKey}
          selectable={props.selectable}
          selectedRowKeys={this.state.selectedRowKeys}
          onChange={this._handleSelectAllChange}
        />
      ),
      cellRenderer: ({ rowData, rowIndex }) => (
        <SelectionCell
          rowData={rowData}
          rowIndex={rowIndex}
          data={this.getExpandedState().expandedData}
          rowKey={props.rowKey}
          selectable={props.selectable}
          selectionBoxProps={props.selectionBoxProps}
          showDisabledSelection={props.showDisabledSelection}
          selectedRowKeys={this.state.selectedRowKeys}
          onChange={this._handleSelectChange}
        />
      ),
      ...props.selectionColumnProps,
      key: SELECTION_COLUMN_KEY
    });
  }

  _insertActionColumn(columns, props) {
    const {
      showSettings,
      actionMode,
      actionOptions,
      actionColumnProps,
      unGroupedText,
      noColumnsFoundText,
      searchColumnsPlaceholderText,
      onRowAction,
      onRowActionShow,
      onRowActionHide
    } = props;
    const showActionCell =
      actionOptions && (actionMode === ActionMode.COLUMN || actionMode === ActionMode.BOTH);
    if (!showSettings && !showActionCell) return;

    columns.push({
      width: DEFAULT_COLUMN_WIDTH,
      flexGrow: 1,
      flexShrink: 0,
      locked: true,
      align: Alignment.RIGHT,
      resizable: false,
      frozen: FrozenDirection.RIGHT,
      /* eslint-disable indent */
      headerRenderer: showSettings
        ? ({ container }) => (
            <ActionHeaderCell
              container={(actionColumnProps && actionColumnProps.container) || container}
              columns={this._getColumns()}
              groups={props.columnGroups}
              freezable={props.fixed}
              columnsReorderable={props.columnsReorderable}
              unGroupedText={unGroupedText || props[TSymbolProp].translate('UNGROUPED')}
              noColumnsFoundText={
                noColumnsFoundText || props[TSymbolProp].translate('NO_COLUMNS_FOUND')
              }
              searchColumnsPlaceholderText={
                searchColumnsPlaceholderText ||
                props[TSymbolProp].translate('COLUMN_SEARCH_PLACEHOLDER')
              }
              onToggle={this._handleColumnToggle}
              onFrozen={this._handleColumnFrozen}
              onReorder={this._handleColumnReorder}
            />
          )
        : null,
      cellRenderer: showActionCell
        ? ({ container, ...rest }) => (
            <ActionCell
              {...rest}
              container={(actionColumnProps && actionColumnProps.container) || container}
              rowKey={props.rowKey}
              actionOptions={actionOptions}
              onRowAction={onRowAction}
              onRowActionShow={onRowActionShow}
              onRowActionHide={onRowActionHide}
            />
          )
        : null,
      /* eslint-enable indent */
      ...props.actionColumnProps,
      key: ACTION_COLUMN_KEY
    });
  }

  /**
   * See BaseTable#getExpandedState
   */
  getExpandedState() {
    return {
      expandedData: this._data,
      expandedDepthMap: {},
      ...(this.table && this.table.getExpandedState()),
      expandedRowKeys: this.state.expandedRowKeys,
      expandedGroupKeys: this.state.expandedGroupKeys,
      groups: this._groups
    };
  }

  /**
   * See BaseTable#getTotalRowsHeight
   */
  getTotalRowsHeight() {
    return this.table ? this.table.getTotalRowsHeight() : 0;
  }

  /**
   * See BaseTable#getTotalColumnsWidth
   */
  getTotalColumnsWidth() {
    return this.table ? this.table.getTotalColumnsWidth() : 0;
  }

  /**
   * See BaseTable#forceUpdateTable
   */
  forceUpdateTable() {
    this.table && this.table.forceUpdateTable();
  }

  /**
   * See BaseTable#scrollToPosition
   */
  scrollToPosition(offset) {
    this.table && this.table.scrollToPosition(offset);
  }

  /**
   * See BaseTable#scrollToTop
   */
  scrollToTop(scrollTop) {
    this.table && this.table.scrollToTop(scrollTop);
  }

  /**
   * See BaseTable#scrollToLeft
   */
  scrollToLeft(scrollLeft) {
    this.table && this.table.scrollToLeft(scrollLeft);
  }

  /**
   * See BaseTable#scrollToRow
   */
  scrollToRow(rowIndex, align) {
    this.table && this.table.scrollToRow(rowIndex, align);
  }

  /**
   * See BaseTable#setExpandedRowKeys
   */
  setExpandedRowKeys(expandedRowKeys) {
    // if `expandedRowKeys` is controlled
    if (this.props.expandedRowKeys !== undefined) return;

    this.setState({
      expandedRowKeys: cloneArray(expandedRowKeys)
    });
  }

  /**
   * Set `expandedGroupKeys` manually.
   *
   * @param {array} expandedGroupKeys
   */
  setExpandedGroupKeys(expandedGroupKeys) {
    this.setState({
      expandedGroupKeys: cloneArray(expandedGroupKeys)
    });
  }

  /**
   * Set `selectedRowKeys` manually.
   * This method is available only if `selectedRowKeys` is uncontrolled.
   *
   * @param {array} selectedRowKeys
   */
  setSelectedRowKeys(selectedRowKeys) {
    // if `selectedRowKeys` is controlled
    if (this.props.selectedRowKeys !== undefined) return;

    this.setState({
      selectedRowKeys: cloneArray(selectedRowKeys)
    });
  }

  /**
   * Remove rowKeys from inner state  manually, it's useful to purge dirty state after rows removed.
   * This method is available only if `selectedRowKeys` or `expandedRowKeys` is uncontrolled.
   *
   * @param {array} rowKeys
   */
  removeRowKeysFromState(rowKeys) {
    if (!Array.isArray(rowKeys)) return;

    const state = {};
    if (this.props.selectedRowKeys === undefined && this.state.selectedRowKeys.length > 0) {
      state.selectedRowKeys = this.state.selectedRowKeys.filter(key => !rowKeys.includes(key));
    }
    if (this.props.expandedRowKeys === undefined && this.state.expandedRowKeys.length > 0) {
      state.expandedRowKeys = this.state.expandedRowKeys.filter(key => !rowKeys.includes(key));
    }
    if (state.selectedRowKeys || state.expandedRowKeys) {
      this.setState(state);
    }
  }

  renderEmpty() {
    const { loading, emptyRenderer, emptyDataText } = this.props;

    if (loading) return null;
    if (emptyRenderer) return renderElement(emptyRenderer);

    return (
      <EmptyState
        className="MatrixTable__empty-content"
        text={emptyDataText || this.props[TSymbolProp].translate('EMPTY_DATA')}
        icon={EmptyState.Icons.EMPTY_BOX}
      />
    );
  }

  renderOverlay() {
    const { loading, loadingMore, loadingMoreText, overlayRenderer } = this.props;

    if (overlayRenderer) return renderElement(overlayRenderer);
    if (loadingMore)
      return (
        <div className="MatrixTable__loading-more-layer">
          <span className="MatrixTable__loading-more-text">
            {loadingMoreText || this.props[TSymbolProp].translate('LOADING_MORE')}
          </span>
          <Loader className="MatrixTable__loading-more-loader" />
        </div>
      );
    if (loading) return <Loader className="MatrixTable__loading-layer" />;

    return null;
  }

  // custom headerRenderer to render column group header
  renderHeader({ cells, columns, headerIndex }) {
    if (headerIndex === this._columnGroupsLevel || columns.length === 0) return cells;

    const groupCells = [];
    const groupTitles = this.props.columnGroups.reduce((titles, group) => {
      titles[group.key] = group.title;
      return titles;
    }, {});
    let width = 0;

    columns.forEach((column, columnIndex) => {
      if (column[BaseTable.PlaceholderKey]) {
        groupCells.push(cells[columnIndex]);
      } else if (column.key === SELECTION_COLUMN_KEY || column.key === ACTION_COLUMN_KEY) {
        groupCells.push(
          <div
            key={`header-group-cell-${column.key}-placeholder`}
            className="MatrixTable__header-cell-placeholder"
            style={cells[columnIndex].props.style}
          />
        );
      } else {
        width += cells[columnIndex].props.style.width;

        const groupLevel = this._columnGroupsLevel - headerIndex - 1;
        const groupKey = this._columnsGroupPath[column.key][groupLevel];
        const nextColumn = columns[columnIndex + 1];
        if (
          columnIndex === columns.length - 1 ||
          groupKey !== this._columnsGroupPath[nextColumn.key][groupLevel]
        ) {
          groupCells.push(
            <div
              key={`header-group-cell-${column.key}`}
              className="MatrixTable__header-cell MatrixTable__header-group-cell"
              style={{ ...cells[columnIndex].props.style, width }}
            >
              <TableHeaderCell
                className="MatrixTable__header-group-cell-text"
                container={this.table}
              >
                {groupTitles[groupKey] || ''}
              </TableHeaderCell>
            </div>
          );
          width = 0;
        }
      }
    });
    return groupCells;
  }

  renderTable = (size, theme) => {
    const {
      className,
      loading,
      loadingMore,
      overlayRenderer,
      expandColumnKey,
      actionOptions,
      actionMode,
      autoGrow,
      ...rest
    } = this.props;

    const columns = this._getColumns();
    const grouping = this._isGrouping();
    const showOverlay = loading || loadingMore || overlayRenderer;
    const showContextMenu =
      actionOptions && (actionMode === ActionMode.CONTEXT || actionMode === ActionMode.BOTH);

    const cls = cn(className, {
      'MatrixTable--show-left-shadow': columns.some(
        column =>
          !column.locked &&
          !column.hidden &&
          (column.frozen === true || column.frozen === FrozenDirection.LEFT)
      ),
      'MatrixTable--show-right-shadow': columns.some(
        column => !column.locked && !column.hidden && column.frozen === FrozenDirection.RIGHT
      ),
      'MatrixTable--grouped': grouping,
      [`MatrixTable--${theme}`]: theme === Themes.ALLOY
    });

    // use `maxHeight` instead of `height` as the container's height in autoGrow mode
    const maxHeight = autoGrow ? size.height : this.props.maxHeight;
    const showColumnGroupHeader = this._showColumnGroupHeader();
    const headerHeight = showColumnGroupHeader
      ? this._getHeaderHeight(
          this.props.headerHeight,
          this.props.headerGroupHeight,
          this._columnGroupsLevel
        )
      : this.props.headerHeight;
    const headerRenderer = showColumnGroupHeader ? this.renderHeader : this.props.headerRenderer;
    const headerClassName = showColumnGroupHeader
      ? this._headerClassName
      : this.props.headerClassName;
    return [
      <BaseTable
        key="base-table"
        ref={this._setTableRef}
        {...rest}
        classPrefix="MatrixTable"
        className={cls}
        data={this._data}
        width={size.width}
        height={size.height}
        maxHeight={maxHeight}
        columns={columns}
        headerHeight={headerHeight}
        headerRenderer={headerRenderer}
        expandColumnKey={expandColumnKey}
        expandedRowKeys={this._getExpandedRowKeys()}
        // trigger table re-render when selectedRowKeys changed
        selectedRowKeys={this.state.selectedRowKeys}
        headerClassName={headerClassName}
        rowClassName={this._rowClassName}
        rowEventHandlers={this._rowEventHandlers}
        components={this._components}
        emptyRenderer={this.renderEmpty}
        overlayRenderer={showOverlay && this.renderOverlay}
        onColumnResize={this._handleColumnResize}
        onEndReached={this._handleEndReached}
        onRowExpand={this._handleRowExpand}
        onExpandedRowsChange={noop}
        headerCellProps={this._headerCellProps}
        rowProps={this._rowProps}
        cellProps={this._cellProps}
      />,
      showContextMenu && (
        <ContextMenu
          key="context-menu"
          ref={this._setMenuRef}
          actionOptions={actionOptions}
          onRowAction={this.props.onRowAction}
        />
      )
    ];
  };

  UNSAFE_componentWillReceiveProps(nextProps) {
    const state = {};

    // scroll to top when reloading data
    if (!this.props.loading && nextProps.loading) {
      this.scrollToTop(0);
    }

    let columns;
    if (
      nextProps.data !== this.props.data ||
      nextProps.selectable !== this.props.selectable ||
      nextProps.showSettings !== this.props.showSettings ||
      nextProps.actionMode !== this.props.actionMode ||
      nextProps.actionOptions !== this.props.actionOptions
    ) {
      columns = this._normalizeColumns(nextProps);

      if (nextProps.actionOptions !== this.props.actionOptions) {
        this._rowEventHandlers = {
          onContextMenu: nextProps.actionOptions ? this._handleRowContextMenu : null,
          ...nextProps.rowEventHandlers
        };
      }
    }

    if (
      !columns &&
      ((nextProps.columns && nextProps.columns !== this.props.columns) ||
        nextProps.children !== this.props.children)
    ) {
      columns = this._normalizeColumns(nextProps);
    }

    if (columns) {
      columns.forEach(column => {
        const { key } = column;
        const cachedColumnState = this._cachedColumnsState[key];
        const columnState = this.state.columnsState[key];

        if (key === nextProps.expandColumnKey) {
          column.toggleable = false;
          column.hidden = false;
        }
        // don't reset column state if those props don't change
        if (cachedColumnState && columnState) {
          if (column.resizable) {
            if (!cachedColumnState.resizable || column.width !== cachedColumnState.width) {
              cachedColumnState.width = column.width;
            } else {
              column.width = columnState.width;
            }
          }

          if (!column.locked && column.toggleable !== false) {
            if (
              cachedColumnState.locked ||
              cachedColumnState.toggleable === false ||
              column.hidden !== cachedColumnState.hidden
            ) {
              cachedColumnState.hidden = column.hidden;
            } else {
              column.hidden = columnState.hidden;
            }
          }

          if (!column.locked) {
            if (cachedColumnState.locked || column.frozen !== cachedColumnState.frozen) {
              cachedColumnState.frozen = column.frozen;
            } else {
              column.frozen = columnState.frozen;
            }
          }

          cachedColumnState.resizable = column.resizable;
          cachedColumnState.locked = column.locked;
          cachedColumnState.toggleable = column.toggleable;
          cachedColumnState.position = column.position;
        }
      });

      const columnsState = columns.reduce((state, column) => {
        const { key, width, hidden, frozen, position } = column;
        state[key] = { width, hidden, frozen, position };
        return state;
      }, {});

      state.columns = columns;
      state.columnsState = columnsState;
      if (nextProps.storageKey) {
        if (nextProps.storageKey !== this.props.storageKey) {
          this.storage = new Storage(nextProps.storageKey);
        }
        this.storage.set('columnsState', columnsState);
      }
    }

    // If `selectedRowKeys` is controlled
    if (
      nextProps.selectable &&
      nextProps.selectedRowKeys !== undefined &&
      nextProps.selectedRowKeys !== this.props.selectedRowKeys
    ) {
      state.selectedRowKeys = cloneArray(nextProps.selectedRowKeys);
    }

    // if `expandedRowKeys` is controlled
    if (
      nextProps.expandColumnKey &&
      nextProps.expandedRowKeys !== undefined &&
      nextProps.expandedRowKeys !== this.props.expandedRowKeys
    ) {
      this.setState({
        expandedRowKeys: cloneArray(nextProps.expandedRowKeys)
      });
    }

    // in grouping mode
    if (!this._isGrouping(nextProps)) {
      this._data = nextProps.data;
      this._groups = [];
      state.expandedGroupKeys = [];
    } else {
      const { data, groupBy, expandColumnKey, emptyGroupText, rowKey } = nextProps;
      if (
        !isObjectEqual(groupBy, this.props.groupBy) ||
        data !== this.props.data ||
        expandColumnKey !== this.props.expandColumnKey
      ) {
        const columns = state.columns || this.state.columns;
        const [grouped, groups] = groupByColumns(
          data,
          groupBy,
          columns,
          expandColumnKey,
          emptyGroupText || nextProps[TSymbolProp].translate('EMPTY_GROUP'),
          rowKey
        );
        this._proxyColumns(columns, nextProps);
        this._data = unflatten(grouped.concat(groups), null, rowKey);
        // keep the collapsed groups
        const prevGroupKeys = this._groups.map(group => group[rowKey]);
        const groupKeys = groups.map(group => group[rowKey]);
        this._groups = groups;
        const collapsedGroupKeys = prevGroupKeys.filter(
          key => !this.state.expandedGroupKeys.includes(key)
        );
        state.expandedGroupKeys = collapsedGroupKeys.length
          ? groupKeys.filter(key => !collapsedGroupKeys.includes(key))
          : groupKeys;
      }
    }

    this.setState(state);
  }

  _isGrouping(props = this.props) {
    const { groupBy, expandColumnKey } = props;
    return !!groupBy && groupBy.length > 0 && !!expandColumnKey;
  }

  _getColumns() {
    const { columns, columnsState } = this.state;
    return this._getColumnsWithState(columns, columnsState, this.props.fixed);
  }

  _getExpandedRowKeys() {
    const { expandedRowKeys, expandedGroupKeys } = this.state;
    if (!this._isGrouping()) return expandedRowKeys;

    return this._concatExpandedKeys(expandedRowKeys, expandedGroupKeys);
  }

  _setTableRef(ref) {
    this.table = ref;
    this.props.setContainerRef(ref);
  }

  _setMenuRef(ref) {
    this.menu = ref;
  }

  _rowClassName({ rowData, rowIndex }) {
    const { rowClassName, rowKey, onRowClick, highlightedRowKeys } = this.props;
    const rowClass = rowClassName ? callOrReturn(rowClassName, { rowData, rowIndex }) : '';
    const key = rowData[rowKey];
    return cn(rowClass, {
      'MatrixTable__row--clickable': !!onRowClick,
      'MatrixTable__row--highlighted': highlightedRowKeys.includes(key),
      'MatrixTable__row--selected': this.state.selectedRowKeys.includes(key),
      'MatrixTable__group-header': isGroupKey(key)
    });
  }

  _headerClassName({ headerIndex }) {
    const { headerClassName } = this.props;
    const headerClass = headerClassName ? callOrReturn(headerClassName, { headerIndex }) : '';
    return cn(headerClass, {
      'MatrixTable__header-group-row': headerIndex === 0
    });
  }

  _selectable = ({ rowData, rowIndex }) => {
    const { selectable, rowKey } = this.props;

    if (isGroupKey(rowData[rowKey])) return false;

    return callOrReturn(selectable, { rowData, rowIndex });
  };

  _handleSelectAllChange(selectedAll, selectedRowKeys) {
    // if `selectedRowKeys` is uncontrolled, update internal state
    if (this.props.selectedRowKeys === undefined) {
      this.setState({ selectedRowKeys });
      this.forceUpdateTable();
    }
    this.props.onSelectedRowsChange(selectedRowKeys);
    this.props.onSelectAll(selectedAll, selectedRowKeys);
  }

  _handleSelectChange({ selected, rowData, rowIndex }) {
    const selectedRowKeys = cloneArray(this.state.selectedRowKeys);
    const key = rowData[this.props.rowKey];

    if (selected) {
      if (!selectedRowKeys.includes(key)) selectedRowKeys.push(key);
    } else {
      const index = selectedRowKeys.indexOf(key);
      if (index > -1) {
        selectedRowKeys.splice(index, 1);
      }
    }
    // if `selectedRowKeys` is uncontrolled, update internal state
    if (this.props.selectedRowKeys === undefined) {
      this.setState({ selectedRowKeys });
      this.forceUpdateTable();
    }
    this.props.onRowSelect({ selected, rowData, rowIndex });
    this.props.onSelectedRowsChange(selectedRowKeys);
  }

  _updateColumnsState(column, key, value) {
    const columnsState = {
      ...this.state.columnsState,
      [column.key]: { ...this.state.columnsState[column.key], [key]: value }
    };
    this.setState({ columnsState });

    this._storeColumnsState(columnsState);
  }

  _storeColumnsState(columnsState) {
    if (this.storage) {
      this.storage.set('columnsState', columnsState);
    }
  }

  _handleColumnResize({ column, width }) {
    this._updateColumnsState(column, 'width', width);
    this.props.onColumnResize({ column, width });
  }

  _handleColumnToggle(column, hidden) {
    this._updateColumnsState(column, 'hidden', hidden);
    this.props.onColumnHide({ column, hidden });
  }

  _handleColumnFrozen(column, frozen) {
    this._updateColumnsState(column, 'frozen', frozen);
    this.props.onColumnFrozen({ column, frozen });
  }

  _handleColumnReorder(source, target) {
    const { columns } = this.state;
    const isMoveUp = source.position > target.position;
    const indexRange = columns.reduce(
      (preVal, column, index) => {
        if ([source.key, target.key].includes(column.key)) {
          preVal.start = Math.min(preVal.start, index);
          preVal.end = Math.max(preVal.end, index);
        }
        return preVal;
      },
      { start: columns.length, end: 0 }
    );

    const prefixColumns = columns.slice(0, indexRange.start);
    const targetColumns = columns.slice(indexRange.start, indexRange.end + 1);
    const suffixColumns = columns.slice(indexRange.end + 1);

    const reorderedColumns = (isMoveUp ? targetColumns : targetColumns.reverse()).reduce(
      (preVal, column, index, array) => {
        if (index + 1 < array.length) {
          column.position = array[index + 1].position;
          preVal.push(column);
        } else {
          column.position = isMoveUp
            ? Math.min(source.position, target.position)
            : Math.max(source.position, target.position);
          preVal.unshift(column);
        }
        return index + 1 === array.length && !isMoveUp ? preVal.reverse() : preVal;
      },
      []
    );

    reorderedColumns.forEach(col => this._updateColumnsState(col, 'position', col.position));

    this.setState({
      columns: [...prefixColumns, ...reorderedColumns, ...suffixColumns]
    });
    this.props.onColumnsPositionChange(reorderedColumns);
  }

  _handleEndReached(args) {
    const { loading, loadingMore, loadedAll, onEndReached } = this.props;
    if (!onEndReached || loading || loadingMore || loadedAll) return;

    onEndReached(args);
  }

  _handleRowContextMenu(args) {
    // grouping or frozen rows
    if (isGroupKey(args.rowData[this.props.rowKey]) || args.rowIndex < 0) return;

    // close action menu if there is any
    if (typeof document !== undefined && document.body) document.body.click();
    this.menu && this.menu.getInstance().open(args);
  }

  _handleRowExpand({ expanded, rowData, rowIndex, rowKey }) {
    if (isGroupKey(rowKey)) {
      const expandedGroupKeys = cloneArray(this.state.expandedGroupKeys);
      if (expanded) {
        if (!expandedGroupKeys.includes(rowKey)) expandedGroupKeys.push(rowKey);
      } else {
        const index = expandedGroupKeys.indexOf(rowKey);
        if (index > -1) {
          expandedGroupKeys.splice(index, 1);
        }
      }
      this.setState({ expandedGroupKeys });
      this.props.onGroupExpand({ expanded, rowData, rowIndex, rowKey });
      return;
    }

    const expandedRowKeys = cloneArray(this.state.expandedRowKeys);
    if (expanded) {
      if (!expandedRowKeys.includes(rowKey)) expandedRowKeys.push(rowKey);
    } else {
      const index = expandedRowKeys.indexOf(rowKey);
      if (index > -1) {
        expandedRowKeys.splice(index, 1);
      }
    }
    // if `expandedRowKeys` is uncontrolled, update internal state
    if (this.props.expandedRowKeys === undefined) {
      this.setState({ expandedRowKeys });
    }
    this.props.onRowExpand({ expanded, rowData, rowIndex, rowKey });
    this.props.onExpandedRowsChange(expandedRowKeys);
  }

  _showColumnGroupHeader() {
    const { fixed, columnGroups, headerHeight, headerGroupHeight, headerRenderer } = this.props;
    const showColumnGroupHeader =
      fixed && columnGroups && !Array.isArray(headerHeight) && !headerRenderer && headerGroupHeight;

    if (showColumnGroupHeader) {
      [this._columnsGroupPath, this._columnGroupsLevel] = mapColumnsGroupPath(
        this._getColumns(),
        columnGroups
      );
    }
    return showColumnGroupHeader;
  }

  _headerCellProps(args) {
    const { headerHeight, headerCellProps, attachTestId } = this.props;
    const props = callOrReturn(headerCellProps, args);

    if (!attachTestId) return props;
    const headerIndex = Array.isArray(headerHeight) ? `-${args.headerIndex}` : '';
    return {
      ...props,
      'data-testid': `MatrixTable__header${headerIndex}-cell-${args.column.key}`
    };
  }

  _rowProps(args) {
    const { rowProps, attachTestId, rowKey } = this.props;
    const props = callOrReturn(rowProps, args);

    if (!attachTestId) return props;
    return {
      ...props,
      'data-testid': `MatrixTable__row-${args.rowData[rowKey]}`
    };
  }

  _cellProps(args) {
    const { cellProps, attachTestId, rowKey } = this.props;
    const props = callOrReturn(cellProps, args);

    if (!attachTestId) return props;
    return {
      ...props,
      'data-testid': `MatrixTable__row-${args.rowData[rowKey]}-cell-${args.column.key}`
    };
  }

  render() {
    const { containerClassName, width, height, maxHeight, autoGrow, onResize } = this.props;

    // we have to give AutoSizer a value to disable height measure if `maxHeight` is set
    const containerHeight = !autoGrow && maxHeight > 0 ? 0 : height;
    return (
      <ThemeConsumer>
        {({ theme }) => {
          const cls = cn('MatrixTable__container', containerClassName, {
            [`MatrixTable__container--${theme}`]: theme === Themes.ALLOY
          });

          return (
            <AutoResizer className={cls} width={width} height={containerHeight} onResize={onResize}>
              {size => this.renderTable(size, theme)}
            </AutoResizer>
          );
        }}
      </ThemeConsumer>
    );
  }
}

MatrixTable.Column = Column;
MatrixTable.SortOrder = SortOrder;
MatrixTable.ActionMode = ActionMode;
MatrixTable.ScrollToRowAlignment = ScrollToRowAlignment;

MatrixTable.defaultProps = {
  ...BaseTable.defaultProps,
  actionMode: ActionMode.BOTH,
  highlightedRowKeys: [],
  headerGroupHeight: 0,
  onRowSelect: noop,
  onGroupExpand: noop,
  onSelectedRowsChange: noop,
  onSelectAll: noop,
  onRowAction: noop,
  onRowActionShow: noop,
  onRowActionHide: noop,
  onResize: noop,
  onColumnHide: noop,
  onColumnFrozen: noop,
  onColumnsPositionChange: noop,
  setContainerRef: noop
};

MatrixTable.propTypes = {
  ...BaseTable.propTypes,
  /**
   * unique key for local persistence of column state(width, hidden, frozen) if set
   */
  storageKey: PropTypes.string,
  /**
   * Class name for the container
   */
  containerClassName: PropTypes.string,
  /**
   * The width of the table, will be the container's width if not set
   */
  width: PropTypes.number,
  /**
   * The height of the table, will be the container's height if not set
   */
  height: PropTypes.number,
  /**
   * The height of the table will grow automatically until fill it's container and `maxHeight` will be ignored if set to `true`,
   * it's similar to `maxHeight` but the `maxHeight` value is the container's height instead of being set manually
   */
  autoGrow: PropTypes.bool,
  /**
   * Whether the table is in loading state
   */
  loading: PropTypes.bool,
  /**
   * Whether all the data is loaded
   */
  loadedAll: PropTypes.bool,
  /**
   * Whether the table is in loading more state
   */
  loadingMore: PropTypes.bool,
  /**
   * Text to show when the table is in loading more state
   */
  loadingMoreText: PropTypes.string,
  /**
   * Text to show when the table data is empty
   */
  emptyDataText: PropTypes.string,
  /**
   * Default group title if `groupKey` is not set
   */
  unGroupedText: PropTypes.string,
  /**
   * Default label for group row if the grouped data's values are empty
   */
  emptyGroupText: PropTypes.string,
  /**
   * Default label for column toggle menu when no columns are found
   */
  noColumnsFoundText: PropTypes.string,
  /**
   * Default label for column toggle search placeholder
   */
  searchColumnsPlaceholderText: PropTypes.string,
  /**
   * The height of the column group header, take effects only if `fixed && columnGroups && !Array.isArray(headerHeight) && !headerRenderer`
   */
  headerGroupHeight: PropTypes.number,
  /**
   * The groups for the columns
   */
  columnGroups: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      parentKey: PropTypes.string
    })
  ),
  /**
   * The column keys or column definition to be grouped on in order, will be ignored if `expandColumnKey` is unset
   */
  groupBy: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.shape(Column.propTypes)])
  ),
  /**
   * A callback function when expand or collapse a group node
   * The handler is of the shape of `({ expanded, rowData, rowIndex, rowKey }) => *`
   */
  onGroupExpand: PropTypes.func,
  /**
   * Controlled highlighted row keys
   */
  highlightedRowKeys: PropTypes.arrayOf(PropTypes.string),
  /**
   * Custom props for the builtin selection column
   */
  selectionColumnProps: PropTypes.object,
  /**
   * Custom props for the builtin action column
   */
  actionColumnProps: PropTypes.object,
  /**
   * Whether to show the settings icon
   */
  showSettings: PropTypes.bool,
  /**
   * Where to show the action options if `actionOptions` is provided
   */
  actionMode: PropTypes.oneOf(Object.values(ActionMode)),
  /**
   * An array of ActionButton's options or a callback to return the options
   * The callback is of the shape of `({ rowData, rowIndex }) => array`
   */
  actionOptions: PropTypes.oneOfType([
    PropTypes.arrayOf(ActionButton.optionPropType),
    PropTypes.func
  ]),
  /**
   * A callback function for an action option click event
   * The handler is of the shape of `({ key, rowData, rowIndex }) => *`
   */
  onRowAction: PropTypes.func,
  /**
   * A callback function when show action menu
   * The handler is of the shape of ({ rowData, rowIndex }) => *
   */
  onRowActionShow: PropTypes.func,
  /**
   * A callback function when hide action menu
   * The handler is of the shape of ({ rowData, rowIndex }) => *
   */
  onRowActionHide: PropTypes.func,
  /**
   * Whether the rows of the table are selectable, could be a callback to decide which row is selectable
   * The callback is of the shape of `({ rowData, rowIndex }) => bool`
   */
  selectable: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
  /**
   * Whether to show the SelectionBox if the row is not selectable
   */
  showDisabledSelection: PropTypes.bool,
  /**
   * Default selected row keys when initalize the table
   */
  defaultSelectedRowKeys: PropTypes.arrayOf(PropTypes.string),
  /**
   * Controlled selected row keys
   */
  selectedRowKeys: PropTypes.arrayOf(PropTypes.string),
  /**
   * A callback function when select or unselect a row
   * The handler is of the shape of `({ selected, rowData, rowIndex }) => *`
   */
  onRowSelect: PropTypes.func,
  /**
   * A callback function when the selected row keys changed
   * The handler is of the shape of `(selectedRowKeys) => *`
   */
  onSelectedRowsChange: PropTypes.func,
  /**
   * A callback function when toggle the checkbox in the header of selection column
   * The handler is of the shape of `(selected, selectedRowKeys) => *`
   */
  onSelectAll: PropTypes.func,
  /**
   * Extra props applied to ExpandIcon component
   * The handler is of the shape of `({ rowData, rowIndex, disabled, checked }) => object`
   */
  selectionBoxProps: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
  /**
   * A callback function when the size of the table container changed if the width and height are not set
   * The handler is of the shape of `({ width, height }) => *`
   */
  onResize: PropTypes.func,
  /**
   * A callback function when click a row
   * The handler is of the shape of `({ column, width }) => *`
   */
  onRowClick: PropTypes.func,
  /**
   * A callback function when toggle the hidden state of a column in the settings panel
   * The handler is of the shape of `({ column, hidden }) => *`
   */
  onColumnHide: PropTypes.func,
  /**
   * A callback function when toggle the frzoen state of a column in the settings panel
   * The handler is of the shape of `({ column, frozen }) => *`
   */
  onColumnFrozen: PropTypes.func,
  /**
   * A callback function to set the ref of the container
   */
  setContainerRef: PropTypes.func,
  /**
   * Whether to attach `data-testid` to elements
   */
  attachTestId: PropTypes.bool,
  /**
   * A callback function when columns position changed in the settings panel.
   * The handler is of the shape of `(changedColumns) => *`
   */
  onColumnsPositionChange: PropTypes.func
};

export default withLocale(
  MatrixTable,
  ({ locale }) => new MDText(require(`./i18n/${locale}.json`))
);
